import React, { useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import feedbackService from '../services/feedbackService';
import { isSessionExpiredError } from '../utils/errorHandling';

const MAX_SUBJECT_LENGTH = 255;
const MAX_MESSAGE_LENGTH = 5000;

/**
 * FeedbackModal - Ermöglicht dem Benutzer, Feedback an das Team zu senden.
 * Zeigt nach erfolgreicher Übermittlung eine Erfolgsmeldung an.
 *
 * @param {boolean} show - Steuert die Sichtbarkeit des Modals
 * @param {Function} onHide - Callback zum Schließen des Modals
 */
const FeedbackModal = ({ show, onHide }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setSubject('');
    setMessage('');
    setLoading(false);
    setSuccess(false);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await feedbackService.submitFeedback(subject, message);
      setSuccess(true);
      setSubject('');
      setMessage('');
    } catch (err) {
      if (isSessionExpiredError(err)) return;
      setError(
        err.response?.data?.message ||
          'Feedback konnte nicht gesendet werden. Bitte versuche es erneut.'
      );
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    subject.trim() !== '' &&
    subject.trim().length <= MAX_SUBJECT_LENGTH &&
    message.trim() !== '' &&
    message.trim().length <= MAX_MESSAGE_LENGTH;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={!loading}
      id="feedback-modal"
      aria-labelledby="feedback-modal-title"
      aria-describedby={!success ? 'feedback-modal-description' : undefined}
    >
      <Modal.Header closeButton>
        <Modal.Title id="feedback-modal-title">Feedback</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!success && (
          <p className="text-muted mb-3" id="feedback-modal-description">
            Hat etwas nicht funktioniert? Fehlt eine Funktion, die für Dich wichtig ist? Hast Du
            einen Verbesserungsvorschlag? Schreib es hier, damit unser Team es umsetzen kann.
          </p>
        )}

        <div role="status" id="feedback-status-region">
          {success && (
            <Alert variant="success" id="feedback-success-alert">
              Vielen Dank für Dein Feedback! Wir werden es sorgfältig prüfen.
            </Alert>
          )}
        </div>

        {error && (
          <Alert variant="danger" id="feedback-error-alert" role="alert">
            {error}
          </Alert>
        )}

        {!success && (
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="feedback-subject-input">
              <Form.Label>Betreff</Form.Label>
              <Form.Control
                type="text"
                placeholder="Worum geht es?"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={MAX_SUBJECT_LENGTH}
                required
                disabled={loading}
                id="feedback-subject-input"
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="feedback-message-textarea">
              <Form.Label>Nachricht</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Beschreibe Dein Anliegen..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={MAX_MESSAGE_LENGTH}
                required
                disabled={loading}
                id="feedback-message-textarea"
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={loading}
                id="feedback-cancel-button"
              >
                Abbrechen
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={loading || !isFormValid}
                id="feedback-submit-button"
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Sende...
                  </>
                ) : (
                  'Senden'
                )}
              </Button>
            </div>
          </Form>
        )}

        {success && (
          <div className="d-flex justify-content-end">
            <Button variant="primary" onClick={handleClose} id="feedback-close-button">
              Schließen
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default FeedbackModal;
